import { getConfig } from '@cian/config/browser';
import { getHttpApi } from '@cian/http-api/browser';
import { getLogger } from '@cian/logger/browser';
import { ErrorLogComponent } from '@cian/error-log-component';
import { getEventBusIfDefined } from '@cian/event-bus';
import { FragmentProvider } from '@cian/fragment/browser';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Provider } from 'react-redux';

import { SearchEngineResultsPageContainer } from '../../shared/common/components/serp/container';
import { createAppStore, ISerializableAppState, IWidgets, IAppState } from '../../shared/common/state';
import { runRegistrationTriggerPopup } from '../../shared/registrationTriggers/signInPopup/utils';
import { createMakeRequest } from '../../shared/utils/request';
import { FastFilters } from '../fast/fast_filters';

import { attachReducers } from '../../shared/common/state/attach_reducers';
import { attachSagas } from '../../shared/common/state/attach_sagas';

import { loadFonts } from '@cian/font-loader/lib/browser';
import { createContext } from '@cian/underground-map-widget';

import { PushContainer } from '../../shared/serp/components/push/container';
import { modifyPageViewAnalyticsEvent } from '../utils/modifyPageViewAnalyticsEvent';
import { mlSearchSession } from '../../shared/mlSearchSession';
import { DynamicCalltrackingContext } from '@cian/frontend-dynamic-calltracking-component';
import { subscribeToInteractiveTime } from './services/subscribeToInteractiveTime';
import { displayConsultantPhone } from '../../shared/utils/displayNewbuildingConsultantPhone';
import { ApplicationContext } from '../../shared/serp/containers/ApplicationContext';

export async function init(state: ISerializableAppState, fastFilters?: FastFilters) {
  const config = getConfig();
  const logger = getLogger();
  const httpApi = getHttpApi();

  const rootElement = document.getElementById('frontend-serp');

  if (!rootElement) {
    throw new Error('Unable to find element #frontend-serp');
  }

  const apiBaseUrl = config.getStrict<string>('apiBaseUrl');
  const subdomain = config.getStrict<string>('subdomain');
  const { jsonQuery } = state.results;

  mlSearchSession.init(jsonQuery);

  const makeRequest = createMakeRequest(httpApi, apiBaseUrl, subdomain);

  const widgets: IWidgets = {
    undergroundMapSchemeWidgetService: createContext({
      loader: {
        loadFonts: () => {
          loadFonts([
            {
              name: 'PTSans',
              weight: 300,
              style: 'normal',
              eot: require('@cian/underground-map-widget/fonts/pt_sans/pt_sans-regular.eot'),
              woff: require('@cian/underground-map-widget/fonts/pt_sans/pt_sans-regular.woff'),
              ttf: require('@cian/underground-map-widget/fonts/pt_sans/pt_sans-regular.ttf'),
            },
          ]);
        },
        loadScheme: locationId => {
          return import(`@cian/underground-map-widget/data/${locationId}.json`);
        },
      },
    }),
  };

  const store = createAppStore({
    config,
    httpApi,
    initialState: state,
    logger,
    makeRequest,
    optionalParams: {
      widgets,
    },
  });

  runRegistrationTriggerPopup(store);
  subscribeToInteractiveTime({ store });

  attachReducers(store);
  attachSagas(store);

  if (fastFilters) {
    fastFilters.getActions().forEach(action => {
      store.dispatch(action);
    });
  }

  /**
   * Обновляем сессию поиска пользователя, когда пользователь попал на ранжированную выдачу И
   * выдача не была закэширована ml-сервисом.
   */
  if (state.mlRankingGuid && !state.isMlRankingResponseCached) {
    mlSearchSession.updateSession(jsonQuery);
  }

  const pageViewParams = {
    page: { extra: { mlSearchSessionGuid: mlSearchSession.getSessionGuid(jsonQuery) } },
  };

  /**
   * Добавляем параметр mlSearchSessionGuid в объект события pageview при первом ренедере страницы.
   * Данный хак требуется, поскольку признак mlSearchSessionGuid существует только на стороне браузера
   * (хранится в sessionStorage).
   */
  modifyPageViewAnalyticsEvent({ logger }, { pageViewParams });

  // Обертка ErrorLogComponent необходима потому, что в react-redux нет componentDidCatch
  ReactDOM.hydrate(
    <ErrorLogComponent logger={logger}>
      <ApplicationContext.Provider value={{ httpApi, config, logger }}>
        <DynamicCalltrackingContext.Provider value={{ logger, httpApi }}>
          <Provider store={store}>
            <FragmentProvider>
              <SearchEngineResultsPageContainer />
            </FragmentProvider>
            <FragmentProvider>
              <PushContainer />
            </FragmentProvider>
          </Provider>
        </DynamicCalltrackingContext.Provider>
      </ApplicationContext.Provider>
    </ErrorLogComponent>,
    rootElement,
    () => {
      try {
        if (fastFilters) {
          fastFilters.runSideeffects();
        }
      } catch (e) {}

      const eventBus = getEventBusIfDefined();
      if (eventBus) {
        eventBus.emit('metrics-loader', {
          type: 'unlock-initialization',
        });
      }

      const consultantPhone = config.get<string>('newbuildingConsultant.phone.moscowAndRegion');
      const consultantPhoneEnabledRegions = config.get<number[]>('newbuildingConsultant.phone.enabledRegions') || [];
      displayConsultantPhone(state as IAppState, consultantPhoneEnabledRegions, consultantPhone);
    },
  );
}

export interface IModule {
  init: typeof init;
}
